import { useCallback, useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { getPresenceSnapshot } from '../../../context/NotificationContext';

const SOCKET_URL = process.env.REACT_APP_BASE_SERVER_URL || 'https://nexploy-eg.com';

/**
 * Messenger Socket.IO connection.
 *
 * The messenger page used to rely solely on its own socket here. In practice
 * the NotificationContext socket (always on at app boot) sometimes wins the
 * race for the user-room emit while this socket is still upgrading — DMs
 * landed in the notification socket and the messenger UI never updated.
 *
 * To make delivery robust we ALSO subscribe to the `hrms:private-message:*`
 * window CustomEvents that NotificationContext re-broadcasts. Either socket
 * arriving the event triggers the same `handleIncomingMessage` flow, and
 * useMessages.js deduplicates by message id so a double-delivery is safe.
 *
 * Handlers are stored in refs and refreshed by the consumer via
 * `updateHandlers()` on every render — without that, the socket listeners
 * close over the first-render handlers (stale `selectedUser` etc.) and the
 * open-chat read-receipt emit never fires.
 */
const useSocket = () => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  // Latest handlers, kept in a ref so the window-event bridge below always
  // calls the freshest closure even if React re-renders the consumer.
  const handlersRef = useRef({});
  const userRef = useRef(null);

  // Public escape hatch so consumers can refresh handlers without tearing
  // down the socket. MessengerPage MUST call this on every render (via a
  // useEffect) — otherwise the listeners below close over the first-render
  // `handlers` object, and `handleIncomingMessage` / `markMessagesAsRead`
  // end up with a stale `selectedUser`/`messages` snapshot.
  const updateHandlers = useCallback((handlers = {}, user) => {
    handlersRef.current = { ...handlersRef.current, ...handlers };
    if (user !== undefined) userRef.current = user;
  }, []);

  const initializeSocket = useCallback((authToken, user, handlers = {}) => {
    handlersRef.current = handlers;
    userRef.current = user;

    // Seed presence from the NotificationContext module cache. That socket
    // received the `users:online` snapshot at app boot — the messenger
    // socket would normally connect AFTER that point and miss it. Without
    // this seed the conversation list shows everyone as offline until they
    // happen to reconnect.
    try {
      const seed = getPresenceSnapshot();
      if (seed.size > 0) handlers.setOnlineUsers?.(seed);
    } catch (_) {
      // Defensive — getPresenceSnapshot is plain data, shouldn't throw, but
      // a stale build / circular-import edge case shouldn't break socket init.
    }

    return new Promise((resolve, reject) => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      const socketInstance = io(SOCKET_URL, {
        auth: { token: authToken },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 3000,
      });

      // All listeners read handlers + user from refs so every event runs
      // against the latest closure, not the one captured at first mount.
      socketInstance.on('connect', () => {
        setIsConnected(true);
        handlersRef.current.setIsConnected?.(true);
        setSocket(socketInstance);
        socketRef.current = socketInstance;
        resolve(socketInstance);
      });

      socketInstance.on('disconnect', () => {
        setIsConnected(false);
        handlersRef.current.setIsConnected?.(false);
      });

      socketInstance.on('connect_error', (error) => {
        handlersRef.current.onError?.('Connection failed. Retrying...');
        reject(error);
      });

      socketInstance.on('private_message:received', (data) => {
        handlersRef.current.handleIncomingMessage?.(data, userRef.current);
      });

      socketInstance.on('chat_group:message', (data) => {
        handlersRef.current.handleIncomingMessage?.(data, userRef.current);

        // Phase 3: Auto-emit delivery confirmation for group messages.
        // When this member receives a group message, tell the server so it
        // can track per-member delivery and update the aggregate status.
        const myCode = userRef.current?.infinityCode;
        const senderCode = data?.senderId || data?.senderCode;
        if (myCode && senderCode && String(myCode) !== String(senderCode) && data.id && data.groupId) {
          socketInstance.emit('chat_group:mark_delivered', {
            messageId: data.id,
            groupId: data.groupId,
          });
        }
      });

      socketInstance.on('chat_group:joined', (data) => {
        handlersRef.current.handleGroupJoined?.(data, userRef.current);
      });

      socketInstance.on('chat_group:members_added', (data) => {
        handlersRef.current.handleGroupMembersAdded?.(data);
      });

      socketInstance.on('chat_group:member_removed', (data) => {
        handlersRef.current.handleGroupMemberRemoved?.(data);
      });

      socketInstance.on('chat_group:removed', (data) => {
        handlersRef.current.handleGroupRemoved?.(data);
      });

      socketInstance.on('private_message:typing', (data) => {
        if (data.isTyping) {
          handlersRef.current.setTypingUsers?.((prev) => new Set(prev).add(data.senderId));
        } else {
          handlersRef.current.setTypingUsers?.((prev) => {
            const newSet = new Set(prev);
            newSet.delete(data.senderId);
            return newSet;
          });
        }
      });

      socketInstance.on('private_messages:read', (data) => {
        handlersRef.current.markMessagesAsRead?.(data.userId);
      });

      // Emoji reactions — both directions carry {messageId, reactions}.
      // Private events also carry senderId/receiverId so the handler can
      // resolve which thread the message lives in; group events carry groupId.
      socketInstance.on('private_message:reaction', (data) => {
        handlersRef.current.handleReactionEvent?.(data, userRef.current);
      });

      socketInstance.on('chat_group:reaction', (data) => {
        handlersRef.current.handleReactionEvent?.(data, userRef.current);
      });

      // Phase 3: Group message status updates (delivered/read aggregate)
      socketInstance.on('chat_group:status_update', (data) => {
        handlersRef.current.handleGroupStatusUpdate?.(data);
      });

      // Group message deletion events
      socketInstance.on('chat_group:message_deleted', (data) => {
        handlersRef.current.handleGroupMessageDeleted?.(data);
      });

      socketInstance.on('users:online', (data) => {
        if (Array.isArray(data)) {
          handlersRef.current.setOnlineUsers?.(new Set(data));
        }
      });

      socketInstance.on('user:online', (data) => {
        handlersRef.current.setOnlineUsers?.((prev) => new Set(prev).add(data.userId));
      });

      socketInstance.on('user:offline', (data) => {
        handlersRef.current.setOnlineUsers?.((prev) => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
      });
    });
  }, []);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    }
  }, []);

  return { socket, isConnected, initializeSocket, updateHandlers, disconnectSocket };
};

export default useSocket;
